/**
 * PlatformLabCommonUsagePanel — §6.6 공통 모듈 시연 패널
 * C025 API 버전 관리 · C023 ID 생성 · C019 비동기 · C018 복원력 · C036 분산 세션 재사용 현황
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

export function PlatformLabCommonUsagePanel() {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);

  const usages = [
    {
      module: 'eraf-core (C025)',
      api: '@ApiVersion(1) / @ApiVersion(2)',
      role: 'v1/v2 echo 병존 매핑 — URL /api/eraf/v{1,2}/platform-lab/echo',
      snippet: '@ApiVersion(2) @GetMapping("/api/eraf/platform-lab/echo")',
    },
    {
      module: 'eraf-core (C025)',
      api: '@DeprecatedApi + DeprecationHeaderWriter',
      role: 'v1 폐기 헤더 자동 방출 (Deprecation/Sunset/Link) — biz 코드 0줄',
      snippet: '@DeprecatedApi(sunset="2026-12-31", link="...")',
    },
    {
      module: 'eraf-core (C023)',
      api: 'IdGenerator.uuid/uuidV7/ulid/shortId/snowflake',
      role: '식별자 5종 라이브 생성 — 알고리즘 재구현 0',
      snippet: 'IdGenerator.ulid()',
    },
    {
      module: 'eraf-core (C023)',
      api: 'SnowflakeIdWorker.nextId()',
      role: 'Snowflake 배치 단조성(monotonic) 시연',
      snippet: 'new SnowflakeIdWorker(1L).nextId()',
    },
    {
      module: 'eraf-core (C019)',
      api: 'erafTaskExecutor (ThreadPoolTaskExecutor)',
      role: '비동기 작업 풀 제출 — core=4/max=20/queue=100',
      snippet: 'executor.submit(new ContextAwareCallable<>(() -> ...))',
    },
    {
      module: 'eraf-core (C019)',
      api: 'ContextAwareCallable / ContextAwareTaskDecorator',
      role: '작업 스레드로 RequestContext(traceId) 전파 시연',
      snippet: 'new ContextAwareCallable<>(() -> { ... workerTrace = ctx.traceId() ... })',
    },
    {
      module: 'eraf-core (C018)',
      api: '@CircuitBreaker(name="external-api", fallbackMethod=...)',
      role: '서킷 CLOSED→OPEN 전이 시연 (in-memory 시뮬레이션)',
      snippet: '@CircuitBreaker(name="external-api") public ResilienceResponse circuitBreaker(...)',
    },
    {
      module: 'eraf-core (C018)',
      api: '@Retry(name="external-api")',
      role: 'maxAttempts=3 재시도 시연',
      snippet: '@Retry(name="external-api") public ResilienceResponse retry(...)',
    },
    {
      module: 'eraf-core (C018)',
      api: '@TimeLimiter(name="external-api")',
      role: '1s 타임아웃 — hang 없음 (TimeLimiter 1s 에 차단)',
      snippet: '@TimeLimiter(name="external-api") public CompletableFuture<...> timeout(...)',
    },
    {
      module: 'eraf-core (C018)',
      api: 'ResilienceFallbacks → BusinessException',
      role: 'CallNotPermitted→PLATLAB-503-001 / Timeout→PLATLAB-504-001',
      snippet: 'CallNotPermitted → throw new BusinessException(CIRCUIT_OPEN)',
    },
    {
      module: 'eraf-core (C002)',
      api: 'ApiResponse.success(...)',
      role: '모든 9 endpoint 표준 응답 래핑',
      snippet: 'return ResponseEntity.ok(ApiResponse.success(dto));',
    },
    {
      module: 'eraf-core (C006)',
      api: 'RequestContext.traceId()',
      role: '메인/작업 스레드 traceId 비교 기준',
      snippet: 'String mainTrace = RequestContextHolder.current().traceId()',
    },
    {
      module: 'eraf-core (C004)',
      api: 'DateUtils.nowUtc() / Preconditions.checkArgument',
      role: '시각 표기 / count/delayMs 범위 검증',
      snippet: 'Preconditions.checkArgument(count >= 1 && count <= 1000, "count 1~1000")',
    },
    {
      module: 'eraf-security (C030)',
      api: '@HasPermission("PLATFORM_LAB_EXECUTE")',
      role: 'POST 데모 메서드 단위 권한 (이중 방어)',
      snippet: '@HasPermission("PLATFORM_LAB_EXECUTE") public ... generateIds(...)',
    },
    {
      module: 'eraf-security (C029)',
      api: 'SecurityChainCustomizer + PathPolicy',
      role: '/platform-lab/** + /v1,v2/platform-lab/** AUTHENTICATED 정책',
      snippet: 'PathPolicy.authenticated("/api/eraf/platform-lab/**")',
    },
    {
      module: 'eraf-swagger (C037)',
      api: '@ApiSuccessResponse',
      role: 'OpenAPI 스키마 (9 endpoint)',
      snippet: '(annotation 1줄)',
    },
    {
      module: 'eraf-session (C036)',
      api: '@EnableRedisHttpSession / RedisSessionRepository',
      role: '세션 Redis 저장·투명 래핑',
      snippet: 'request.getSession().setAttribute(...)',
    },
    {
      module: 'eraf-session (C036)',
      api: 'ErafCookieSerializer',
      role: 'SameSite=Strict 세션 쿠키',
      snippet: '(자동)',
    },
    {
      module: 'eraf-data-redis (C050)',
      api: 'StringRedisTemplate',
      role: 'eraf:session:* 키 SCAN 조회',
      snippet: 'redis.scan(...)',
    },
  ];

  return (
    <div
      style={{
        marginTop: 'var(--spacing-6)',
        border: '1px solid var(--color-surface-border)',
        borderRadius: 'var(--radius-md)',
        overflow: 'hidden',
        background: 'var(--color-surface-background)',
      }}
    >
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
        aria-controls="platform-lab-common-usage-table"
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: 'var(--spacing-3) var(--spacing-4)',
          background: 'var(--color-surface-muted)',
          border: 'none',
          cursor: 'pointer',
          fontFamily: 'var(--font-family-sans)',
          fontSize: 'var(--font-size-sm)',
          fontWeight: 600,
          color: 'var(--color-text-secondary)',
          textAlign: 'left',
        }}
      >
        <span>▾ {t('platformLab.commonUsage.title')}</span>
        <span style={{ fontSize: 'var(--font-size-xs)' }}>
          {t('platformLab.commonUsage.description')}
        </span>
      </button>

      {expanded && (
        <div id="platform-lab-common-usage-table" style={{ overflowX: 'auto' }}>
          <table
            style={{
              width: '100%',
              borderCollapse: 'collapse',
              fontSize: 'var(--font-size-xs)',
            }}
            aria-label={t('platformLab.commonUsage.title')}
          >
            <thead>
              <tr style={{ background: 'var(--color-surface-muted)' }}>
                {[
                  t('platformLab.commonUsage.col.module'),
                  t('platformLab.commonUsage.col.api'),
                  t('platformLab.commonUsage.col.role'),
                  t('platformLab.commonUsage.col.snippet'),
                ].map((header) => (
                  <th
                    key={header}
                    style={{
                      padding: 'var(--spacing-2) var(--spacing-3)',
                      textAlign: 'left',
                      borderBottom: '1px solid var(--color-surface-border)',
                      color: 'var(--color-text-secondary)',
                      fontWeight: 600,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {usages.map((row, i) => (
                <tr
                  key={i}
                  style={{
                    background:
                      i % 2 === 0
                        ? 'var(--color-surface-background)'
                        : 'var(--color-surface-muted)',
                  }}
                >
                  <td
                    style={{
                      padding: 'var(--spacing-2) var(--spacing-3)',
                      borderBottom: '1px solid var(--color-surface-border)',
                      fontWeight: 600,
                      color: 'var(--color-brand-primary)',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {row.module}
                  </td>
                  <td
                    style={{
                      padding: 'var(--spacing-2) var(--spacing-3)',
                      borderBottom: '1px solid var(--color-surface-border)',
                      fontFamily: 'var(--font-family-mono)',
                      color: 'var(--color-text-primary)',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {row.api}
                  </td>
                  <td
                    style={{
                      padding: 'var(--spacing-2) var(--spacing-3)',
                      borderBottom: '1px solid var(--color-surface-border)',
                      color: 'var(--color-text-secondary)',
                      minWidth: 200,
                    }}
                  >
                    {row.role}
                  </td>
                  <td
                    style={{
                      padding: 'var(--spacing-2) var(--spacing-3)',
                      borderBottom: '1px solid var(--color-surface-border)',
                      fontFamily: 'var(--font-family-mono)',
                      color: 'var(--color-text-muted)',
                      maxWidth: 320,
                      overflowWrap: 'break-word',
                    }}
                  >
                    {row.snippet}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
